import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { siteGroupExperienceAtom } from '@/store/siteGroupExperienceAtom';
import { getSystemConfig, setSystemConfig } from '@/lib/systemConfig';
import { lookupService } from '@/services/lookupService';

// Two distinct client-side config hierarchies, matching Gen2's GetConfigValue
// and GetSystemConfigValue. Both return the raw string value — callers coerce
// to the type they expect (e.g. `value === 'Y'` for Y/N booleans, `Number(value)`
// for ints, etc.) so the hooks stay agnostic to the key's underlying type.
//
// AppConfig (keys like PPGetHelpAccessEnabled, theme tokens):
//   1. siteGroupExperienceAtom.configOverride[key]      (pp_configuration_override)
//   2. undefined                                         (appsettings.json not client-visible)
//
// SystemConfig (keys like PortalMaxSearchDays, PPAllowAddExamAndSchedule):
//   1. siteGroupExperienceAtom.systemConfigOverride[key] (portal_system_configuration_override)
//   2. getSystemConfig(key)                              (l_system_config, fetched at boot)
//   3. undefined
//
// Both re-render via useAtomValue when the selected facility changes.

export function useConfig(key: string): string | undefined {
  const experience = useAtomValue(siteGroupExperienceAtom);
  const override = experience?.configOverride?.[key];
  if (override === undefined || override === null || override === '') return undefined;
  return String(override);
}

export function useSystemConfig(key: string): string | undefined {
  const experience = useAtomValue(siteGroupExperienceAtom);
  // Subscribe to the bootstrap query so a late SystemConfig fetch re-renders
  // callers (localStorage writes alone do not). Same queryKey as SystemConfigBootstrap.
  const { data } = useQuery({
    queryKey: ['lookup', 'systemConfig'],
    queryFn: async () => {
      const items = await lookupService.getSystemConfig();
      setSystemConfig(items);
      return items;
    },
    staleTime: 15 * 60 * 1000,
  });
  const override = experience?.systemConfigOverride?.[key];
  if (override !== undefined && override !== null && override !== '') return String(override);
  const fromQuery = data?.find((item) => item.key === key)?.value;
  if (fromQuery != null && fromQuery !== '') return String(fromQuery);
  return getSystemConfig(key);
}

/**
 * Truthy check matching the backend Y/N convention — accepts Y, y, Yes, YES,
 * True, true, TRUE, On, 1 (case-insensitive, whitespace-trimmed). Use for
 * boolean flags: `isYes(useConfig('PPHelpEnabled'))`.
 */
export function isYes(value: string | undefined): boolean {
  const v = value?.trim().toUpperCase();
  return v === 'Y' || v === 'YES' || v === 'TRUE' || v === 'ON' || v === '1';
}

export function isNo(value: string | undefined): boolean {
  const v = value?.trim().toUpperCase();
  return v === 'N' || v === 'NO' || v === 'FALSE' || v === 'OFF' || v === '0';
}

export function isEnabledByDefault(value: string | undefined): boolean {
  return !isNo(value);
}

/**
 * Display name for the AI assistant. Defaults to "Aria"; practices can rebrand
 * by setting `pp_configuration_override.AriaName = "Eva"` (or whatever).
 * Pass it as the `name` interpolation to t() calls so the persona renames in
 * every locale at once.
 */
export function useAriaName(): string {
  return useConfig('AriaName') ?? 'Aria';
}
